import React, { createContext, useContext, useEffect, useState, useMemo } from 'react'

export type Theme = 'light' | 'dark' | 'system'

interface ThemeContextType {
    theme: Theme
    setTheme: (theme: Theme) => void
    isDark: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [theme, setThemeState] = useState<Theme>(() => {
        const stored = localStorage.getItem('procenlogs-theme')
        return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system'
    })

    // Track system preference as independent state
    const [systemPrefersDark, setSystemPrefersDark] = useState(
        () => window.matchMedia('(prefers-color-scheme: dark)').matches
    )

    const setTheme = (newTheme: Theme) => {
        setThemeState(newTheme)
        localStorage.setItem('procenlogs-theme', newTheme)
    }

    // Listen for system preference changes
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches)
        mediaQuery.addEventListener('change', handler)
        return () => mediaQuery.removeEventListener('change', handler)
    }, [])

    // Derive isDark from theme + system preference (no extra state/effect)
    const isDark = theme === 'dark' || (theme === 'system' && systemPrefersDark)

    // Apply theme to DOM
    useEffect(() => {
        const root = window.document.documentElement

        if (isDark) {
            root.classList.add('dark')
            document.body.classList.add('dark')
            root.style.colorScheme = 'dark'
        } else {
            root.classList.remove('dark')
            document.body.classList.remove('dark')
            root.style.colorScheme = 'light'
        }

        const effectiveTheme = isDark ? 'dark' : 'light'
        window.dispatchEvent(new CustomEvent('theme-changed', { detail: { theme, effectiveTheme } }))
    }, [isDark, theme])

    const value = useMemo(() => ({ theme, setTheme, isDark }), [theme, isDark])

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    )
}

export function useTheme() {
    const context = useContext(ThemeContext)
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return context
}
